'use client';
import React, { useEffect, useState } from 'react';

export default function Hero() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="relative min-h-screen bg-white pt-16 lg:pt-[72px] overflow-hidden">
      {/* Sky-blue background panel - right side */}
      <div className="absolute top-0 right-0 w-[46%] h-full bg-primary/25 rounded-bl-[80px] lg:rounded-bl-[100px]" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-14 lg:pt-24 pb-16">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left Content */}
          <div
            className={`transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-primary/20 border border-primary/50 rounded-full px-4 py-1.5 mb-7">
              <div className="w-2 h-2 bg-primary rounded-full animate-pulse-soft" />
              <span className="font-inter text-xs font-semibold text-dark/70 tracking-wide">
                Trusted Dental Care · DrPals.in
              </span>
            </div>

            {/* Headline */}
            <h1 className="font-unbounded font-black text-dark leading-[1.08] text-4xl lg:text-5xl xl:text-[56px] mb-6">
              Your Perfect
              <br />
              Smile Starts
              <br />
              <span className="relative inline-block">
                with DrPal's
                <span className="absolute -bottom-1 left-0 w-full h-[3px] bg-primary rounded-full" />
              </span>
            </h1>

            {/* Subtext */}
            <p className="font-inter text-dark/55 text-base lg:text-lg leading-relaxed mb-10 max-w-[420px]">
              Advanced prosthodontic & implant care with a gentle touch.
              Book your appointment with Dr. Abhishek Pal today.
            </p>

            {/* Cards Row */}
            <div className="flex flex-col sm:flex-row gap-4 max-w-md">
              {/* Working Hours Card */}
              <div className="bg-white rounded-3xl p-5 shadow-card border border-secondary flex-1">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-7 h-7 bg-secondary rounded-lg flex items-center justify-center">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" stroke="#131c15" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <p className="font-unbounded font-bold text-dark text-xs">Working Hours</p>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="font-inter text-xs text-dark/50">Mon – Fri</span>
                    <span className="font-inter text-xs font-semibold text-dark bg-secondary px-2 py-0.5 rounded-full">9AM – 8PM</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-inter text-xs text-dark/50">Saturday</span>
                    <span className="font-inter text-xs font-semibold text-dark bg-secondary px-2 py-0.5 rounded-full">10AM – 6PM</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-inter text-xs text-dark/50">Sunday</span>
                    <span className="font-inter text-xs font-semibold text-dark bg-secondary px-2 py-0.5 rounded-full">By Appt.</span>
                  </div>
                </div>
              </div>

              {/* Book Appointment Card */}
              <div
                className="bg-primary rounded-3xl p-5 flex flex-col items-center justify-center gap-3 flex-1 cursor-pointer hover:bg-primary-dark transition-all duration-300 hover:shadow-teal group"
                onClick={() => {
                  const el = document.querySelector('#contact');
                  if (el) el?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-soft group-hover:scale-110 transition-transform duration-300">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M7 17L17 7M17 7H7M17 7V17" stroke="#131c15" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <p className="font-unbounded font-bold text-dark text-sm text-center leading-snug">
                  Book an
                  <br />
                  Appointment
                </p>
              </div>
            </div>
          </div>

          {/* Right - Patient Image */}
          <div
            className={`relative transition-all duration-700 delay-200 ${
              visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
            }`}
          >
            <div className="relative">
              {/* Main image container */}
              <div className="relative rounded-[40px] overflow-hidden aspect-[4/5] max-w-md mx-auto lg:max-w-none shadow-card-hover">
                <img
                  src="https://images.unsplash.com/photo-1606811841689-23dfddce3e95?w=700&q=85"
                  alt="Happy patient with a beautiful smile at DrPal's Dentistry"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-dark/10 to-transparent" />
              </div>

              {/* Floating stat card */}
              <div className="absolute -bottom-4 -left-4 lg:-left-8 bg-white rounded-2xl p-4 shadow-card-hover animate-float">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="#131c15" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-unbounded font-black text-dark text-lg leading-none">500+</p>
                    <p className="font-inter text-xs text-dark/50 mt-0.5">Happy Patients</p>
                  </div>
                </div>
              </div>

              {/* Floating experience badge */}
              <div className="absolute -top-4 -right-4 lg:-right-6 bg-dark rounded-2xl p-4 shadow-card-hover">
                <p className="font-unbounded font-black text-white text-2xl leading-none">5+</p>
                <p className="font-inter text-xs text-white/60 mt-1">Years Exp.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div
          className={`mt-16 lg:mt-20 grid grid-cols-2 lg:grid-cols-4 gap-3 transition-all duration-700 delay-400 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {[
            { value: '500+', label: 'Patients Treated' },
            { value: '5+', label: 'Years Experience' },
            { value: '4', label: 'Specializations' },
            { value: '100%', label: 'Patient Satisfaction' },
          ]?.map((stat) => (
            <div
              key={stat?.label}
              className="bg-secondary border border-secondary-dark rounded-2xl p-5 text-center hover:bg-primary/30 hover:border-primary/50 transition-all duration-300 group"
            >
              <p className="font-unbounded font-black text-dark text-2xl lg:text-3xl">{stat?.value}</p>
              <p className="font-inter text-xs text-dark/50 mt-1.5">{stat?.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
